/**
 * Script per controllare se nel carrello ci sono gift card
 * Effettua una fetch per recuperare i dati del carrello
 * Disabilita l'acquisto di gift card se nel carrello ci sono altri prodotti
 */

(function () {
  "use strict";

  /**
   * Controlla se nel carrello ci sono gift card e altri prodotti
   * @returns {Promise<Object>} Oggetto con proprietà: hasGiftCards, hasOtherProducts, etc
   */
  async function checkGiftCardsInCart() {
    try {
      // Fetch dei dati del carrello
      const response = await fetch(`${window.Shopify.routes.root}cart.json`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cartData = await response.json();

      // Array per memorizzare le gift card trovate
      const giftCards = [];
      const otherProducts = [];

      // Verifica ogni item nel carrello
      if (cartData.items && cartData.items.length > 0) {
        cartData.items.forEach((item) => {
          // Controlla se il prodotto è una gift card
          if (isGiftCard(item)) {
            giftCards.push({
              id: item.id,
              title: item.title,
              quantity: item.quantity,
              price: item.price,
              variant_id: item.variant_id,
            });
          } else {
            otherProducts.push({
              id: item.id,
              title: item.title,
              quantity: item.quantity,
              price: item.price,
              variant_id: item.variant_id,
            });
          }
        });
      }

      const result = {
        hasGiftCards: giftCards.length > 0,
        hasOtherProducts: otherProducts.length > 0,
        giftCards: giftCards,
        otherProducts: otherProducts,
        cartData: cartData,
        totalItems: cartData.item_count,
        cartTotal: cartData.total_price,
      };

      //console.log("Gift Card Check Result:", result);
      return result;
    } catch (error) {
      console.error("Errore nel controllo delle gift card:", error);
      return {
        hasGiftCards: false,
        hasOtherProducts: false,
        giftCards: [],
        otherProducts: [],
        error: error.message,
      };
    }
  }

  /**
   * Verifica se un item è una gift card
   * @param {Object} item - Item del carrello
   * @returns {boolean} True se è una gift card
   */
  function isGiftCard(item) {
    // Controlla il type del prodotto
    const productType = item.product_type || "";
    if (productType.toLowerCase() === "gift_card" || productType.toLowerCase() === "gift card") {
      return true;
    }

    // Controlla il titolo per parole chiave comuni
    if (item.title) {
      const titleLower = item.title.toLowerCase();
      const giftCardKeywords = [
        "gift card",
        "buono regalo",
        "carta regalo",
        "regalo",
      ];

      if (giftCardKeywords.some((keyword) => titleLower.includes(keyword))) {
        return true;
      }
    }

    return false;
  }

  /**
   * Gestisce l'interfaccia di buy-buttons in base allo stato del carrello
   */
  function updateBuyButtonsState() {
    // Seleziona tutti gli elementi buy-buttons che sono gift card
    const buyButtonsElements = document.querySelectorAll(
      'buy-buttons[data-gift-card="true"]'
    );

    if (buyButtonsElements.length === 0) return;

    checkGiftCardsInCart().then((cartState) => {
      buyButtonsElements.forEach((buyButtonsEl) => {
        const submitButton = buyButtonsEl.querySelector(
          'button[type="submit"]'
        );
        const warningEl = buyButtonsEl.querySelector(".gift-card-cart-warning");
        const shopifyPaymentButton = buyButtonsEl.querySelector(".shopify-payment-button");

        // Se nel carrello ci sono altri prodotti
        if (cartState.hasOtherProducts) {
          // Disabilita il button
          if (submitButton) {
            submitButton.disabled = true;
            submitButton.setAttribute("aria-disabled", "true");
          }

          // Disabilita il checkout button
          if (shopifyPaymentButton) {
            shopifyPaymentButton.style.display = "none";
          }

          // Mostra o crea il warning
          if (warningEl) {
            warningEl.style.display = "block";
          } else {
            createWarningElement(buyButtonsEl);
          }
        } else {
          // Abilita il button
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.setAttribute("aria-disabled", "false");
          }

          // Nascondi il warning
          if (warningEl) {
            warningEl.style.display = "none";
          }
        }
      });
    });
  }

  /**
   * Gestisce lo stato del cart drawer (disabilita checkout se ci sono prodotti misti)
   */
  function updateCartDrawerState() {
    checkGiftCardsInCart().then((cartState) => {
      const cartDrawer = document.querySelector("cart-drawer");
      
      if (!cartDrawer) return;

      // Se nel carrello ci sono sia gift card che altri prodotti
      if (cartState.hasGiftCards && cartState.hasOtherProducts) {
        // Disabilita il button di checkout
        const checkoutButton = cartDrawer.querySelector(
          ".cart-drawer__footer .button-group > .button"
        );
        const checkoutButtonGroup = cartDrawer.querySelector(
          ".cart-drawer__footer .button-group"
        );
        
        if (checkoutButton) {
          checkoutButton.classList.add("button--disabled");
          checkoutButton.setAttribute("aria-disabled", "true");
        }

        // Crea o mostra il warning in cima al cart drawer
        let warningEl = cartDrawer.querySelector(".gift-card-cart-warning");
        
        if (!warningEl) {
          warningEl = document.createElement("div");
          warningEl.className = "gift-card-cart-warning";
          warningEl.setAttribute("role", "alert");
          warningEl.innerHTML = `<p>${checkoutButtonGroup.dataset.warningMessage}</p>`;
          
          // Inserisci il warning in cima al cart drawer
          const cartDrawerContent = cartDrawer.querySelector(".cart-drawer__footer");
          if (cartDrawerContent) {
            cartDrawerContent.insertBefore(warningEl, cartDrawerContent.firstChild);
          } else {
            cartDrawer.insertBefore(warningEl, cartDrawer.firstChild);
          }
        } else {
          warningEl.style.display = "block";
        }
      } else {
        // Abilita il button di checkout
        const checkoutButton = cartDrawer.querySelector(
          ".cart-drawer__footer .button-group > .button"
        );
        
        if (checkoutButton) {
          checkoutButton.disabled = false;
          checkoutButton.setAttribute("aria-disabled", "false");
        }

        // Nascondi il warning
        const warningEl = cartDrawer.querySelector(".gift-card-cart-drawer-warning");
        if (warningEl) {
          warningEl.style.display = "none";
        }
      }
    });
  }

  /**
   * Crea un elemento di warning
   * @param {HTMLElement} buyButtonsEl - L'elemento buy-buttons
   */
  function createWarningElement(buyButtonsEl) {
    const warningEl = document.createElement("div");
    warningEl.className = "gift-card-cart-warning";
    warningEl.setAttribute("role", "alert");
    warningEl.innerHTML = "<p>" + getWarningMessage() + "</p>";
    buyButtonsEl.appendChild(warningEl);
  }

  /**
   * Recupera il messaggio di warning dalla pagina (dal data attribute del buy-buttons)
   * @returns {string} Messaggio di warning
   */
  function getWarningMessage() {
    const buyButtonsEl = document.querySelector(
      'buy-buttons[data-gift-card="true"]'
    );
    if (buyButtonsEl && buyButtonsEl.dataset.warningMessage) {
      return buyButtonsEl.dataset.warningMessage;
    }
    return "Cannot purchase gift cards with other products";
  }

  /**
   * Funzione di callback per quando viene aggiunto un item al carrello
   */
  function onCartUpdate() {
    checkGiftCardsInCart().then((result) => {
      // Dispatch evento custom per notificare altre parti del sito
      const event = new CustomEvent("giftCardStatusChanged", {
        detail: result,
      });
      document.dispatchEvent(event);

      // Aggiorna lo stato dei buy buttons
      updateBuyButtonsState();

      // Aggiorna lo stato del cart drawer
      updateCartDrawerState();

      // Esempi di azioni personalizzate
      //if (result.hasGiftCards) {
      //  console.log(
      //    `${result.giftCards.length} gift card trovate nel carrello`
      //  );
      //}
    });
  }

  /**
   * Osserva quando nuovi elementi buy-buttons vengono aggiunti al DOM (utile per modal e template)
   */
  function observeNewBuyButtons() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            // Controlla se il nodo aggiunto è un buy-buttons con data-gift-card
            if (node.nodeType === 1) {
              // Node.ELEMENT_NODE
              if (
                node.matches &&
                node.matches('buy-buttons[data-gift-card="true"]')
              ) {
                // Aspetta un tick affinché l'elemento sia completamente renderizzato
                setTimeout(() => updateBuyButtonsState(), 100);
                setTimeout(() => updateCartDrawerState(), 100);
              }
              // Controlla anche i figli del nodo aggiunto
              if (
                node.querySelector &&
                node.querySelector('buy-buttons[data-gift-card="true"]')
              ) {
                setTimeout(() => updateBuyButtonsState(), 100);
                setTimeout(() => updateCartDrawerState(), 100);
              }
            }
          });
        }
      });
    });

    // Osserva l'intero documento
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Inizializza il monitoraggio del carrello
   */
  function init() {
    // Controllo iniziale
    updateBuyButtonsState();
    updateCartDrawerState();

    // Osserva quando vengono aggiunti nuovi elementi (es. da modal o template)
    observeNewBuyButtons();

    // Monitoraggio degli aggiornamenti del carrello
    // Shopify dispatchs 'cart:change' event quando il carrello cambia
    if (document.addEventListener) {
      document.addEventListener("cart:change", onCartUpdate);
    }

    // Ascolta il cambio di variante sui product form
    document.addEventListener("change", (event) => {
      // Controlla se è un cambio di variante
      if (event.target.name === "id" || event.target.closest('variant-picker')) {
        // Aspetta un attimo che l'elemento sia aggiornato
        setTimeout(() => {
          updateBuyButtonsState();
          updateCartDrawerState();
        }, 50);
      }
    });

    // Ascolta anche il custom event di shopify per variant changes
    document.addEventListener("variant:change", () => {
      setTimeout(() => {
        updateBuyButtonsState();
        updateCartDrawerState();
      }, 50);
    });

    // Esporta le funzioni globalmente se necessario
    window.checkGiftCardsInCart = checkGiftCardsInCart;
    window.updateBuyButtonsState = updateBuyButtonsState;
    window.updateCartDrawerState = updateCartDrawerState;
  }

  // Inizializza quando il DOM è pronto
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
